import type { AxiosRequestConfig } from "axios";

import { getPaginationDataFromHeaders } from "shared/helpers/paginationHeaders";
import ApiClient from "shared/services/api/ApiClient";
import {
  type Nullable,
  type QuerySnippet,
  type QuerySnippetDetails,
  type QuerySnippetGroup,
  type QuerySnippetQueryAttributes,
  QuerySnippetType,
  type SharedWithUsers,
  type SortingOrder,
  type StreamGroup,
} from "shared/types";

export { QuerySnippetType };

export type QuerySnippetsSortBy = "label" | "notes" | "group_label";

export interface ScheduledReportSpecification {
  id: number;
  subject: string;
}

export interface LinkedStream {
  group: Nullable<Pick<StreamGroup, "id" | "label">>;
  id: number;
  label: string;
  scheduled_report_specifications: ScheduledReportSpecification[];
}

export interface GetQuerySnippetsParams {
  organisation_team_id?: number;
  page?: number;
  page_size?: number;
  search?: string;
  sort_by?: QuerySnippetsSortBy;
  sort_direction?: SortingOrder;
}

export interface GetQuerySnippetsResponse {
  page_number: number;
  page_size: number;
  query_snippets: QuerySnippet[];
  total_count: number;
  total_pages: number;
}

interface GetQuerySnippetsApiResponse {
  query_snippets: QuerySnippet[];
}

export interface GetQuerySnippetGroupsParams {
  organisation_team_id?: number;
}

export interface GetQuerySnippetGroupsResponse {
  query_snippet_groups: QuerySnippetGroup[];
}

interface GetLinkedStreamsResponse {
  streams: LinkedStream[];
}

export interface CreateQuerySnippetParams extends QuerySnippetQueryAttributes {
  group?: Nullable<QuerySnippetGroup>;
  label: string;
  notes?: Nullable<string>;
  organisation_team_id?: number;
}

export type UpdateQuerySnippetParams = CreateQuerySnippetParams;

export interface TransferOwnershipParams {
  new_owner_id: number;
  organisation_team_id?: number;
}

export interface ShareQuerySnippetParams {
  user_accessibles: readonly Pick<
    SharedWithUsers,
    "access_level" | "user_id"
  >[];
}

class QuerySnippetsService extends ApiClient {
  constructor() {
    super("/query_snippets");
  }

  async createQuerySnippet(
    type: QuerySnippetType,
    params: CreateQuerySnippetParams
  ): Promise<QuerySnippetDetails> {
    const response = await this.post<QuerySnippetDetails>(`/${type}`, {
      params,
    });

    return response.data;
  }

  async deleteQuerySnippet(type: QuerySnippetType, id: number): Promise<void> {
    await this.delete<void>(`/${type}/${id}`);
  }

  async duplicateQuerySnippet(
    type: QuerySnippetType,
    id: number
  ): Promise<QuerySnippetDetails> {
    const { data } = await this.post<QuerySnippetDetails>(
      `/${type}/${id}/duplicate`
    );

    return data;
  }

  async getLinkedStreams(
    type: QuerySnippetType,
    id: number,
    options: AxiosRequestConfig = {}
  ): Promise<LinkedStream[]> {
    const { data } = await this.get<GetLinkedStreamsResponse>(
      `/${type}/${id}/streams`,
      options
    );

    return data.streams;
  }

  async getQuerySnippet(
    type: QuerySnippetType,
    id: number,
    options: AxiosRequestConfig = {}
  ): Promise<QuerySnippetDetails> {
    const { data } = await this.get<QuerySnippetDetails>(
      `/${type}/${id}`,
      options
    );

    return data;
  }

  async getQuerySnippetGroups(
    type: QuerySnippetType,
    organisationTeamId?: number
  ): Promise<QuerySnippetGroup[]> {
    const params: GetQuerySnippetGroupsParams = organisationTeamId
      ? { organisation_team_id: organisationTeamId }
      : {};

    const response = await this.get<GetQuerySnippetGroupsResponse>(
      `/groups/${type}`,
      {
        params,
      }
    );

    return response.data.query_snippet_groups;
  }

  async getQuerySnippets(
    type: QuerySnippetType,
    params: GetQuerySnippetsParams,
    options: AxiosRequestConfig = {}
  ): Promise<GetQuerySnippetsResponse> {
    const response = await this.get<GetQuerySnippetsApiResponse>(`/${type}`, {
      ...options,
      params,
    });

    const pagination = getPaginationDataFromHeaders(response.headers);

    if (!pagination) {
      throw new Error("Pagination headers are missing");
    }

    return {
      page_number: pagination.page,
      page_size: pagination.pageSize,
      query_snippets: response.data.query_snippets,
      total_count: pagination.totalItems,
      total_pages: pagination.pageCount,
    };
  }

  async removeAccess(type: QuerySnippetType, id: number): Promise<void> {
    await this.delete<void>(`/${type}/${id}/remove_access`);
  }

  async shareQuerySnippet(
    type: QuerySnippetType,
    id: number,
    params: ShareQuerySnippetParams
  ): Promise<QuerySnippetDetails> {
    const { data } = await this.post<QuerySnippetDetails>(
      `/${type}/${id}/share`,
      { params }
    );

    return data;
  }

  async transferOwnership(
    type: QuerySnippetType,
    id: number,
    params: TransferOwnershipParams
  ): Promise<void> {
    await this.post<void>(`/${type}/${id}/transfer_ownership`, { params });
  }

  async updateQuerySnippet(
    type: QuerySnippetType,
    id: number,
    params: UpdateQuerySnippetParams
  ): Promise<QuerySnippetDetails> {
    const response = await this.put<QuerySnippetDetails>(`/${type}/${id}`, {
      params,
    });

    return response.data;
  }
}

export default new QuerySnippetsService();
